import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Download, FileText, X } from 'lucide-react';
import type { SharedFile, SharedFileSet } from '@jobos/shared';
import { NEWMAN_COMPANY, NEWMAN_LOGO_BASE64 } from '@jobos/shared';

import { apiClient, apiUrl } from '@/api/client';
import { Button } from '@/components/ui/button';

// What a client sees when they open a share link. The only page in the app
// outside ProtectedRoute — the token in the URL is the whole credential.
//
// Deliberately plain: a header saying who sent it and which property it is
// about, the files, and two ways to get them. No app chrome, no navigation,
// nothing to sign into.

/** Byte URLs are plain hrefs, so they go through apiUrl rather than apiClient. */
function fileUrl(token: string, fileId: string, kind: 'thumb' | 'view' | 'download'): string {
  return apiUrl(`/share/${token}/files/${fileId}/${kind}`);
}

function formatSize(bytes: number | null): string | null {
  if (bytes == null) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function SharedFilesPage() {
  const { token } = useParams<{ token: string }>();
  const [viewing, setViewing] = useState<SharedFile | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['sharedFiles', token],
    queryFn: () => apiClient.get<SharedFileSet>(`/share/${token}`),
    enabled: !!token,
    // A dead link stays dead; retrying a 404 only delays the message.
    retry: false,
  });

  if (isLoading) {
    return <Centered>Loading…</Centered>;
  }

  // Unknown and revoked are the same 404 from the API, and the same sentence
  // here: whoever holds the link cannot do anything about either.
  if (isError || !data || !token) {
    return (
      <Centered>
        <p className="font-medium text-gray-900">This link is no longer available.</p>
        <p className="mt-1 text-sm text-gray-600">
          Ask {NEWMAN_COMPANY.name} for a new one.
        </p>
      </Centered>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-4 px-4 py-4">
          <img
            src={`data:image/jpeg;base64,${NEWMAN_LOGO_BASE64}`}
            alt=""
            className="h-12 w-auto"
          />
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-lg font-semibold text-gray-900">{NEWMAN_COMPANY.name}</h1>
            {data.address && <p className="truncate text-sm text-gray-600">{data.address}</p>}
            <p className="text-sm text-gray-500">
              {data.files.length} {data.files.length === 1 ? 'file' : 'files'}
            </p>
          </div>
          {data.files.length > 0 && (
            <Button asChild>
              {/* A plain link, not a fetch: the zip streams, and letting the
                  browser own the download keeps a 200-file archive off the heap
                  of this tab. */}
              <a href={apiUrl(`/share/${token}/zip`)}>
                <Download className="mr-2 h-4 w-4" />
                Download all
              </a>
            </Button>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-6">
        {data.files.length === 0 ? (
          <p className="text-sm text-gray-600">There are no files in this link.</p>
        ) : (
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {data.files.map((file) => (
              <SharedFileTile
                key={file.id}
                token={token}
                file={file}
                onOpen={() => setViewing(file)}
              />
            ))}
          </ul>
        )}
      </main>

      {viewing && (
        <SharedFileViewer token={token} file={viewing} onClose={() => setViewing(null)} />
      )}
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="text-center text-gray-700">{children}</div>
    </div>
  );
}

function SharedFileTile({
  token,
  file,
  onOpen,
}: {
  token: string;
  file: SharedFile;
  onOpen: () => void;
}) {
  const size = formatSize(file.sizeBytes);

  return (
    <li className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      {file.isImage ? (
        <button
          type="button"
          onClick={onOpen}
          className="block w-full"
          aria-label={`View ${file.fileName}`}
        >
          <img
            src={fileUrl(token, file.id, 'thumb')}
            alt={file.fileName}
            loading="lazy"
            className="aspect-square w-full bg-gray-100 object-cover"
          />
        </button>
      ) : (
        <div className="flex aspect-square w-full items-center justify-center bg-gray-100">
          <FileText className="h-10 w-10 text-gray-400" />
        </div>
      )}

      <div className="flex items-center gap-2 px-2 py-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-medium text-gray-900">{file.fileName}</p>
          {size && <p className="text-xs text-gray-500">{size}</p>}
        </div>
        <a
          href={fileUrl(token, file.id, 'download')}
          aria-label={`Download ${file.fileName}`}
          className="rounded p-1 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
        >
          <Download className="h-4 w-4" />
        </a>
      </div>
    </li>
  );
}

/**
 * One photo, full size.
 *
 * Not the in-app lightbox: that one pages a roll, presigns variants and knows
 * about jobs. Here there is one image and two things to do with it, so a plain
 * overlay is the whole feature.
 */
function SharedFileViewer({
  token,
  file,
  onClose,
}: {
  token: string;
  file: SharedFile;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/90"
      role="dialog"
      aria-label={file.fileName}
      onClick={onClose}
    >
      <div
        className="flex items-center gap-2 px-4 py-3 text-white"
        onClick={(event) => event.stopPropagation()}
      >
        <p className="min-w-0 flex-1 truncate text-sm">{file.fileName}</p>
        <a
          href={fileUrl(token, file.id, 'download')}
          aria-label={`Download ${file.fileName}`}
          className="rounded p-2 hover:bg-white/10"
        >
          <Download className="h-5 w-5" />
        </a>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="rounded p-2 hover:bg-white/10"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex min-h-0 flex-1 items-center justify-center p-4">
        <img
          src={fileUrl(token, file.id, 'view')}
          alt={file.fileName}
          className="max-h-full max-w-full object-contain"
          onClick={(event) => event.stopPropagation()}
        />
      </div>
    </div>
  );
}
